import base64 from 'base-64'

import {
  PERSONAL_ROOM_PASSWORD_REGEX,
  ROOM_MEETING_NAME_REGEX,
  DID_STORAGE_NAME,
  RESERVED_URL_CHARACTERS_REGEX,
  CURRENT_SCRIPT_SRC_ORIGIN,
  LOBBY_TYPE
} from '../constants'
import { logError } from '~/core/logger'
import { logInfo } from '../logger'

export const getFullNameFromUrl = url =>
  decodeURIComponent(new URL(url).pathname.slice(1))
export const getNameFromUrl = url => {
  const pathname = new URL(url).pathname.slice(1)
  const segments = pathname.split('/')

  return decodeURIComponent(segments[segments.length - 1])
}

export const isValidCustomRoomName = input =>
  !RESERVED_URL_CHARACTERS_REGEX.test(input)
export const isValidPersonalRoomName = input =>
  ROOM_MEETING_NAME_REGEX.test(input)
export const isValidRoomPassword = input =>
  !input || PERSONAL_ROOM_PASSWORD_REGEX.test(input)

export const getConfigsFromUrl = input => {
  let result = {
    config: {},
    interfaceConfig: {}
  }

  if (!input) {
    return result
  }

  const paramsStartIndex = input.search(/\?|#/)
  if (paramsStartIndex < 0) {
    return result
  }

  const queryParams = input.substring(paramsStartIndex + 1, input.length)
  const queryParamsDecoded = decodeURIComponent(queryParams)
  result = queryParamsDecoded.split('&').reduce((acc, next) => {
    const keyValue = next.split('=')
    const key = keyValue[0].split('.')
    const parentPropName = key[0]
    if (parentPropName === DID_STORAGE_NAME) {
      [, acc[parentPropName]] = keyValue
      return acc
    }
    if (
      key.length !== 2 ||
      !['config', 'interfaceConfig'].includes(parentPropName)
    ) {
      return acc
    }

    const childPropName = key[1]
    try {
      acc[parentPropName][childPropName] = JSON.parse(keyValue[1])
    } catch (err) {
      logError({ __filename, keyValue, err }, 'Error parsing value')
    }

    return acc
  }, result)

  return result
}

export const normalizeRoomName = input => {
  const paramsStartIndex = input.search(/\?|#/)
  let result = input

  if (paramsStartIndex > -1) {
    result = input.substring(0, paramsStartIndex)
  }

  return result
}

export const generateGUID = () => {
  //eslint-disable-line
  let d = new Date().getTime()

  // check for node environment
  if (
    typeof window !== 'undefined' &&
    window.performance &&
    typeof window.performance.now === 'function'
  ) {
    d += window.performance.now() // use high-precision timer if available
  }

  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (d + Math.random() * 16) % 16 | 0; // eslint-disable-line
    d = Math.floor(d / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); // eslint-disable-line
  })

  return uuid
}

export const noop = () => { }

export const throttle = (func, limit) => {
  let lastFunc
  let lastRan

  // eslint-disable-next-line func-names
  return function (...args) {
    if (lastRan) {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(this, args)
          lastRan = Date.now()
        }
      }, limit - (Date.now() - lastRan))
    } else {
      func.apply(this, args)
      lastRan = Date.now()
    }
  }
}

export const getLocale = () =>
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.userLanguage || navigator.language || navigator.browserLanguage

export const base64UrlDecode = str => base64.decode(
  str
    .replace(/\./g, '=')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
)

const parseToken = token => {
  const tokenParts = token.split('.')
  if (tokenParts.length < 2) {
    return null
  }

  const base64Url = tokenParts[1]
  const base64String = base64UrlDecode(base64Url)

  return JSON.parse(base64String)
}

export const getJitsiUserIdentity = token => {
  const parsedToken = parseToken(token)

  if (!parsedToken) {
    return null
  }

  return parsedToken.context?.user
}

export const getUserIdentity = token => {
  const parsedToken = parseToken(token)
  if (!parsedToken) {
    return null
  }

  const { sub: userId, cid: customerId } = parsedToken
  return {
    customerId,
    userId
  }
}

export const isTokenExpired = token => {
  if (!token) {
    return true
  }

  try {
    const parsedToken = parseToken(token)
    const { exp } = parsedToken

    // Return true if token has less than 1 min left
    return exp - Math.ceil(Date.now() / 1000) <= 60
  } catch (err) {
    return true
  }
}

export const loadScript = src =>
  new Promise((resolve, reject) => {
    const script = Object.assign(document.createElement('script'), {
      src,
      async: true,
      onerror: reject,
      onload: resolve
    })

    document.head.appendChild(script)
  })

export const getJitsiApi = async (host, roomName) => {
  logInfo({ __filename, host }, 'Loading Jitsi iframe API')

  if (window.JitsiMeetExternalAPI) {
    return window.JitsiMeetExternalAPI
  }

  try {
    let externalApiHost = host
    const parts = roomName.split('/')

    // Extract tenant and use it to load external_api.
    if (parts[1]) {
      externalApiHost = `${host}/${parts[0]}`
    }

    await loadScript(`https://${externalApiHost}/external_api.js`)
    logInfo({ __filename }, 'Successfully loaded Jitsi iframe API')

    return window.JitsiMeetExternalAPI
  } catch (err) {
    throw Error('Cannot load Jitsi iframe API')
  }
}

export const getUtcString = dateLike => new Date(dateLike).toUTCString()

export const isIOSSafari = () => {
  const ua = window.navigator.userAgent
  const iOS = /iPad/i.test(ua) || /iPhone/i.test(ua)
  const webkit = /WebKit/i.test(ua)
  return iOS && webkit && !/CriOS/i.test(ua)
}

export const isIOSWebView = () =>
  (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i).test(navigator.userAgent) ||
  (!!window.webkit && !!window.webkit.messageHandlers)

export const formatDuration = seconds => {
  const hours = (seconds / 3600).toFixed(0)
  const minutes = ((seconds - hours * 3600) / 60).toFixed(0)

  return `${hours}h ${minutes}min`
}

export const downloadFile = ({ url, isElectron, fileName }) => {
  const link = document.createElement('a')
  if (fileName) {
    link.download = fileName
  }
  link.href = url
  if (!isElectron) {
    link.target = '_blank'
  }
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const shareWebViewBlob = async (blob, fileName) => {
  const file = new File([blob], fileName)

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file]
      })
    } catch {}
  }
}

export const getCurrentScriptChartUrl = () => {
  switch (CURRENT_SCRIPT_SRC_ORIGIN) {
    case 'https://api-vo-pilot.jitsi.net':
      return 'https://api-vo-pilot.jitsi.net/meet-proxy'
    case 'https://api-vo.jitsi.net':
      return 'https://api-vo.jitsi.net/meet-proxy'
    default:
      return CURRENT_SCRIPT_SRC_ORIGIN
  }
}

export const accurateInterval = (callback, interval = 1000) => {
  let counter = 1
  let timeoutId
  const startTime = Date.now()

  const main = () => {
    const nowTime = Date.now()
    const nextTime = startTime + counter * interval
    timeoutId = setTimeout(main, interval - (nowTime - nextTime))

    counter += 1
    callback()
  }

  timeoutId = setTimeout(main, interval)

  return {
    clear: () => clearTimeout(timeoutId)
  }
}

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 B'
  const k = 1000
  const dm = decimals || 2
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
}

export const getInitials = name =>
  name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()

export const getLobbySettings = payload => {
  if (payload.waitForApproval) {
    return {
      lobbyEnabled: true,
      lobbyType: LOBBY_TYPE.WAIT_FOR_APPROVAL
    }
  }
  if (payload.waitForModerator) {
    return {
      lobbyEnabled: true,
      lobbyType: LOBBY_TYPE.WAIT_FOR_MODERATOR
    }
  }
  if (payload.waitForApproval === false && payload.waitForModerator === false) {
    return {
      lobbyEnabled: false,
      lobbyType: null
    }
  }
  return {}
}
